import { ForwardedRef, MouseEventHandler, ReactNode } from "react";
import { SearchIcon } from "@chakra-ui/icons";
import {
  CloseButton,
  Input,
  InputGroup,
  InputGroupProps,
  InputLeftElement,
  InputProps,
  InputRightElement,
  forwardRef,
} from "@chakra-ui/react";
import { string } from "yup";

export type SearchInputProps = InputProps & {
  inputGroupProps?: InputGroupProps;
  leftElement?: ReactNode;
  rightElement?: ReactNode;
};

const SearchInput = forwardRef<SearchInputProps, "input">(
  (
    {
      inputGroupProps,
      leftElement = <SearchIcon transform="scaleX(-1)" color="magenta.dark" />,
      rightElement,
      ...inputProps
    },
    ref,
  ) => (
    <InputGroup size="sm" width="100%" {...inputGroupProps}>
      {leftElement && <InputLeftElement>{leftElement}</InputLeftElement>}
      <Input
        ref={ref}
        {...inputProps}
        placeholder={inputProps.placeholder ?? "Search…"}
      />
      {rightElement && <InputRightElement>{rightElement}</InputRightElement>}
    </InputGroup>
  ),
);

export default SearchInput;

type ClearableSearchInputProps = SearchInputProps & {
  onClear: MouseEventHandler<HTMLButtonElement>;
  isClearDisabled?: boolean;
};

function ClearableSearchInputInner(
  { onClear, isClearDisabled, ...rest }: ClearableSearchInputProps,
  ref: ForwardedRef<HTMLInputElement>,
) {
  return (
    <SearchInput
      {...rest}
      ref={ref}
      rightElement={
        <CloseButton
          onClick={onClear}
          borderRadius="full"
          size="sm"
          color="gray"
          isDisabled={isClearDisabled}
        />
      }
    />
  );
}

const ClearableSearchInput = forwardRef(ClearableSearchInputInner);
export { ClearableSearchInput };

export const searchSchema = string()
  .trim()
  .transform((v: unknown) => (v === "" ? null : v))
  .label("Search")
  .nullable()
  .defined()
  .default(null)
  .min(2);
